import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { Reveal, MaskedLines } from "@/components/motion";
import CTABand from "@/components/CTABand";
import { ARTICLES } from "@/data/content";

const Artikel = () => {
  const { slug } = useParams();
  const article = ARTICLES.find((a) => a.slug === slug);

  if (!article) return <Navigate to="/viden" replace />;

  const related = ARTICLES.filter((a) => a.slug !== slug).slice(0, 2);

  return (
    <main data-testid="page-artikel">
      <article>
        <header className="border-b border-hairline">
          <div className="mx-auto max-w-4xl px-4 pb-14 pt-32 sm:px-6 sm:pt-40 lg:px-8 lg:pb-20 lg:pt-48">
            <Reveal y={14}>
              <Link
                to="/viden"
                data-testid="artikel-back-link"
                className="inline-flex items-center gap-3 font-mono text-xs uppercase tracking-[0.2em] text-muted transition-colors hover:text-charcoal"
              >
                <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
                Tilbage til Viden
              </Link>
            </Reveal>
            <Reveal y={14} delay={0.1}>
              <p className="mt-10 font-mono text-[11px] uppercase tracking-[0.25em] text-muted">
                {article.category} · {article.date} · {article.readTime}
              </p>
            </Reveal>
            <h1
              data-testid="artikel-title"
              className="mt-6 font-serif text-4xl leading-[1.08] tracking-tight text-charcoal sm:text-5xl lg:text-6xl"
            >
              <MaskedLines lines={[article.title]} delay={0.2} />
            </h1>
            <Reveal delay={0.55} className="mt-8">
              <p className="max-w-2xl text-lg font-light leading-relaxed text-graphite sm:text-xl">
                {article.excerpt}
              </p>
            </Reveal>
          </div>
        </header>

        <div className="mx-auto max-w-5xl px-4 pt-14 sm:px-6 lg:px-8">
          <Reveal>
            {article.image ? (
              <figure>
                <div className="relative aspect-[21/10] overflow-hidden border border-sand">
                  <img
                    src={article.image}
                    alt={article.title}
                    data-testid="artikel-hero-img"
                    className="img-editorial-soft absolute inset-0 h-full w-full object-cover"
                  />
                </div>
                {article.imageCaption && (
                  <figcaption className="mt-3 font-mono text-[11px] uppercase tracking-[0.2em] text-muted">
                    {article.imageCaption}
                  </figcaption>
                )}
              </figure>
            ) : (
              <div className="border-b border-hairline" />
            )}
          </Reveal>
        </div>

        <div className="mx-auto max-w-3xl px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
          {article.sections.map((s, i) => (
            <Reveal key={i} delay={0.05}>
              {s.heading && (
                <h2 className="mb-5 mt-12 font-serif text-2xl tracking-tight text-charcoal sm:text-3xl">
                  {s.heading}
                </h2>
              )}
              {s.paragraphs.map((p, j) => (
                <p
                  key={j}
                  className={`mb-5 text-base leading-relaxed text-graphite ${i === 0 && j === 0 ? "dropcap" : ""}`}
                >
                  {p}
                </p>
              ))}
            </Reveal>
          ))}
          <Reveal className="mt-14">
            <p className="border-t border-hairline pt-8 text-xs leading-relaxed text-muted">
              Denne artikel er udgivet af Center for Hjerneregulering med henblik på generel viden og
              inspiration. Den er ikke medicinsk rådgivning og kan ikke erstatte samtale med læge eller
              anden behandler.
            </p>
          </Reveal>
        </div>
      </article>

      <section className="border-t border-hairline bg-surface">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8">
          <Reveal>
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-muted">Læs videre</p>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 gap-10 md:grid-cols-2">
            {related.map((a) => (
              <Reveal key={a.slug}>
                <Link to={`/viden/${a.slug}`} data-testid={`artikel-related-${a.slug}`} className="group block">
                  {a.image && (
                    <div className="relative aspect-[16/9] overflow-hidden border border-sand">
                      <img
                        src={a.image}
                        alt={a.title}
                        loading="lazy"
                        className="img-editorial-soft absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                      />
                    </div>
                  )}
                  <p className="mt-5 font-mono text-[11px] uppercase tracking-[0.25em] text-muted">
                    {a.category} · {a.readTime}
                  </p>
                  <h3 className="mt-3 font-serif text-2xl leading-[1.15] tracking-tight text-charcoal sm:text-3xl">
                    {a.title}
                  </h3>
                  <span className="mt-4 inline-flex items-center gap-3 font-mono text-xs uppercase tracking-[0.2em] text-charcoal">
                    <span className="border-b border-charcoal pb-1">Læs artiklen</span>
                    <ArrowUpRight
                      className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                      strokeWidth={1.5}
                    />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTABand
        title="Vil du høre, hvad det betyder for dig?"
        sub="Book en indledende samtale. Jeg sætter ord på, hvordan hjernetræning kan passe ind i netop din hverdag."
      />
    </main>
  );
};

export default Artikel;
